import { withRetry } from './retry';

export const FILE_IMPORT_ERROR = 'Import failed';
const FILE_RETRY_OPTIONS = { retries: 2, baseDelayMs: 600, maxDelayMs: 20_000, jitterMs: 300 };

// One queue per import batch, shared by all submissions and attachments.
export const createSubmissionFileImporter = (request, { isCancelled = () => false } = {}) => {
    const queue = [];
    let active = 0;

    const importFile = async (body) => {
        try {
            const data = await withRetry(() => {
                if (isCancelled()) {
                    throw Object.assign(new Error('Import cancelled'), { data: { retryable: false } });
                }
                return request(body, { isCancelled });
            }, FILE_RETRY_OPTIONS);
            return { data, error: null };
        } catch (_error) {
            return {
                error: FILE_IMPORT_ERROR,
                data: {
                    name: body.submission?.fileName || body.submission?.FileName || 'downloaded_file',
                    type: body.submission?.['content-type'] || 'application/octet-stream',
                    // A failed file has no uploaded bytes and must not inflate the folder size.
                    size: 0,
                },
            };
        }
    };

    const runNext = () => {
        if (active >= 10 || queue.length === 0) return;
        const { body, resolve, reject } = queue.shift();
        active += 1;
        // Keep the slot until both retries and any timeout-recovery polling finish.
        importFile(body)
            .then(resolve, reject)
            .finally(() => {
                active -= 1;
                runNext();
            });
    };

    return (body) =>
        new Promise((resolve, reject) => {
            queue.push({ body, resolve, reject });
            runNext();
        });
};

export const buildSubmissionImportFolders = (results, submissions) =>
    results.map((files, index) => {
        const firstFile = Array.isArray(files) ? files[0] : null;
        if (firstFile) {
            return {
                id: firstFile.api.user.id,
                name: firstFile.api.user.name,
                files,
                submitted_at: firstFile.submitted_at,
            };
        }

        // An empty or malformed submission must not discard other students' files.
        const submission = submissions[index];
        return {
            id: submission?.user_id ?? submission?.Entity?.EntityId ?? `failed-submission-${index}`,
            name: submission?.user?.name || submission?.Entity?.DisplayName || `Submission ${index + 1}`,
            files: [],
            error: FILE_IMPORT_ERROR,
        };
    });
